/**
 * Map job progress units → IntentProgressCircle segment arcs.
 * Equal-weight units; QUEUED units are empty (track shows through).
 */

import { sqColorPair } from "./sq-colors";

export type JobProgressUnitStatus =
	| "QUEUED"
	| "RUNNING"
	| "SUCCEEDED"
	| "FAILED"
	| "CANCELLED";

export interface JobProgressUnitLike {
	id?: string;
	label?: string;
	status: JobProgressUnitStatus;
}

export interface JobProgressUnitsMetaLike {
	totalUnits: number;
	units: JobProgressUnitLike[];
	progressPercent?: number;
}

export interface JobProgressCircleSegment {
	/** Share of the ring (0–100). */
	value: number;
	color: string;
	gradientEnd?: string;
	/** When true, segment is not painted (queued gap). */
	empty?: boolean;
	status: JobProgressUnitStatus;
}

export interface JobProgressCountLayer {
	/** Share of the track/ring (0–100). */
	value: number;
	color: string;
	gradientEnd?: string;
}

export interface JobProgressCountLayers {
	processing: JobProgressCountLayer;
	/** Value may be 0 while no units are terminal yet — keep mounted for animation. */
	terminal: JobProgressCountLayer;
	terminalStatus: "none" | "success" | "mixed" | "failure";
}

export const JOB_PROGRESS_RING_MAX_DISPLAY_SEGMENTS = 24;

export const JOB_PROGRESS_UNIT_STATUS_COLORS: Record<
	Exclude<JobProgressUnitStatus, "QUEUED">,
	{ from: string; to: string }
> = {
	SUCCEEDED: {
		from: sqColorPair("green", "static").from,
		to: sqColorPair("green", "static").to,
	},
	FAILED: {
		from: sqColorPair("red", "static").from,
		to: sqColorPair("red", "static").to,
	},
	RUNNING: {
		from: sqColorPair("orange", "static").from,
		to: sqColorPair("orange", "static").to,
	},
	CANCELLED: {
		from: sqColorPair("grey", "static").from,
		to: sqColorPair("grey", "static").to,
	},
};

const JOB_PROGRESS_PROCESSING_LAYER = {
	from: "rgba(20, 25, 36, 0.22)",
	to: "rgba(20, 25, 36, 0.22)",
};

const STATUS_COLORS = JOB_PROGRESS_UNIT_STATUS_COLORS;

function unitToSegment(
	status: JobProgressUnitStatus,
	value: number,
): JobProgressCircleSegment {
	if (status === "QUEUED") {
		return {
			value,
			color: "transparent",
			empty: true,
			status: "QUEUED",
		};
	}
	const pair = STATUS_COLORS[status];
	return {
		value,
		color: pair.from,
		gradientEnd: pair.to,
		status,
	};
}

interface StatusGroup {
	status: JobProgressUnitStatus;
	count: number;
}

function groupConsecutive(
	units: JobProgressUnitLike[],
): StatusGroup[] {
	const grouped: StatusGroup[] = [];
	for (const unit of units) {
		const last = grouped[grouped.length - 1];
		if (last && last.status === unit.status) {
			last.count += 1;
		} else {
			grouped.push({ status: unit.status, count: 1 });
		}
	}
	return grouped;
}

/**
 * Collapse consecutive same-status units for header ring display.
 * If still over max, merge oldest SUCCEEDED groups into one, then QUEUED.
 */
export function coalesceJobProgressUnitsForDisplay(
	units: JobProgressUnitLike[],
	maxSegments = JOB_PROGRESS_RING_MAX_DISPLAY_SEGMENTS,
): StatusGroup[] {
	let grouped = groupConsecutive(units);
	if (grouped.length <= maxSegments) {
		return grouped;
	}

	const mergeStatus = (status: JobProgressUnitStatus) => {
		const next: StatusGroup[] = [];
		let bucket = 0;
		for (const group of grouped) {
			if (group.status === status) {
				bucket += group.count;
				continue;
			}
			if (bucket > 0) {
				next.push({ status, count: bucket });
				bucket = 0;
			}
			next.push(group);
		}
		if (bucket > 0) {
			next.push({ status, count: bucket });
		}
		grouped = next;
	};

	mergeStatus("SUCCEEDED");
	if (grouped.length > maxSegments) {
		mergeStatus("QUEUED");
	}
	if (grouped.length > maxSegments) {
		mergeStatus("CANCELLED");
	}

	while (grouped.length > maxSegments) {
		const first = grouped[0];
		const second = grouped[1];
		if (!first || !second) {
			break;
		}
		grouped = [
			{
				status: first.status,
				count: first.count + second.count,
			},
			...grouped.slice(2),
		];
	}

	return grouped;
}

export function jobProgressUnitsToCircleSegments(
	meta: JobProgressUnitsMetaLike,
	options?: { maxDisplaySegments?: number },
): JobProgressCircleSegment[] {
	const total = Math.max(meta.units.length, 1);
	const max = options?.maxDisplaySegments;
	if (max != null && meta.units.length > max) {
		const groups = coalesceJobProgressUnitsForDisplay(meta.units, max);
		return groups.map((group) =>
			unitToSegment(group.status, (group.count / total) * 100),
		);
	}
	const weight = 100 / total;
	return meta.units.map((unit) => unitToSegment(unit.status, weight));
}

/**
 * Linear bar segments from equal-weight units (same colors as the header ring).
 * QUEUED segments render as empty track gaps.
 */
export function jobProgressUnitsToBarSegments(
	meta: JobProgressUnitsMetaLike,
): JobProgressCircleSegment[] {
	return jobProgressUnitsToCircleSegments(meta);
}

/**
 * Convert equal-weight unit status into layered count progress:
 * - processing: all non-QUEUED units (grey base)
 * - terminal: all terminal units, colored by aggregate outcome so far
 */
export function jobProgressUnitsToCountLayers(
	meta: JobProgressUnitsMetaLike,
): JobProgressCountLayers {
	const total = Math.max(meta.totalUnits, meta.units.length, 1);
	const processingCount = meta.units.filter(
		(unit) => unit.status !== "QUEUED",
	).length;
	const successCount = meta.units.filter(
		(unit) => unit.status === "SUCCEEDED",
	).length;
	const failureCount = meta.units.filter(
		(unit) => unit.status === "FAILED" || unit.status === "CANCELLED",
	).length;
	const terminalCount = successCount + failureCount;

	let terminalStatus: JobProgressCountLayers["terminalStatus"] = "none";
	if (terminalCount > 0) {
		terminalStatus =
			successCount > 0 && failureCount > 0
				? "mixed"
				: successCount > 0
					? "success"
					: "failure";
	}

	const terminalColors =
		terminalStatus === "success"
			? STATUS_COLORS.SUCCEEDED
			: terminalStatus === "mixed"
				? STATUS_COLORS.RUNNING
				: terminalStatus === "failure"
					? STATUS_COLORS.FAILED
					: null;

	const terminalValue = (terminalCount / total) * 100;
	const resolvedTerminalColors =
		terminalColors ?? STATUS_COLORS.SUCCEEDED;

	return {
		processing: {
			value: (processingCount / total) * 100,
			color: JOB_PROGRESS_PROCESSING_LAYER.from,
			gradientEnd: JOB_PROGRESS_PROCESSING_LAYER.to,
		},
		terminal: {
			value: terminalValue,
			color: resolvedTerminalColors.from,
			gradientEnd: resolvedTerminalColors.to,
		},
		terminalStatus,
	};
}

/** Always return processing + terminal slots so layered UI can animate 0→N%. */
export function jobProgressCountLayersToCircleLayers(
	layers: JobProgressCountLayers,
	options?: { strokeWidth?: number },
): Array<{
	id: string;
	value: number;
	color: string;
	gradientEnd?: string;
	strokeWidth?: number;
}> {
	return [
		{
			id: "processing",
			value: layers.processing.value,
			color: layers.processing.color,
			gradientEnd: layers.processing.gradientEnd,
			strokeWidth: options?.strokeWidth,
		},
		{
			id: "terminal",
			value: layers.terminal.value,
			color: layers.terminal.color,
			gradientEnd: layers.terminal.gradientEnd,
			strokeWidth: options?.strokeWidth,
		},
	];
}

export function jobProgressCountLayersToBarLayers(
	layers: JobProgressCountLayers,
): Array<{
	id: string;
	value: number;
	style: { background: string };
}> {
	return [
		{
			id: "processing",
			value: layers.processing.value,
			style: gradientStyle(
				layers.processing.color,
				layers.processing.gradientEnd,
			),
		},
		{
			id: "terminal",
			value: layers.terminal.value,
			style: gradientStyle(layers.terminal.color, layers.terminal.gradientEnd),
		},
	];
}

function gradientStyle(
	from: string,
	to?: string,
): { background: string } {
	return {
		background: to
			? `linear-gradient(90deg, ${from}, ${to})`
			: from,
	};
}
